import { type RefObject, useCallback, useId } from "react";

import { makeToast } from "@moment/design-system/Toast";

import { log } from "~/utils/datadog/datadog";

export const useCreatePrintAreaCallback = (contentDiv: RefObject<HTMLElement | null>) => {
	const id = useId();

	return useCallback(() => {
		const container = contentDiv.current;

		if (!container) {
			makeToast({
				message: "Could not find container to print",
				variant: "error",
				timer: "timed-dismissible",
			});
			log(
				"Could not find container to print",
				{
					container,
				},
				"error"
			);
			return;
		}

		// copy html content
		const htmlContent = `${container.innerHTML}`;

		// copy all styles
		const styles = Array.from(document.styleSheets);

		const features = [
			`scrollbars=no`,
			`resizable=no`,
			`status=no`,
			`location=no`,
			`toolbar=no`,
			`menubar=no`,
			`width=700`,
			`height=800`,
			`left=0`,
			`top=0`,
		];

		const onWindowLoad = (w: Window) => () => {
			// just in case: don't run this function twice!
			const hasLoadedKey = `__PRINT_AREA_${id}__`;
			const hasLoaded = w[hasLoadedKey];

			// if we've already loaded, then exit
			if (hasLoaded === "loaded") {
				return;
			}

			Object.assign(w, { [hasLoadedKey]: "loaded" });

			// find body classes
			const bodyClasses = w.document.body.classList;

			// remove all theme classes
			for (const classEntry in bodyClasses) {
				if (classEntry.includes("theme-")) {
					w.document.body.classList.remove(classEntry);
				}
			}

			// use light-theme for printing
			w.document.body.classList.add("theme-light");

			// remove bg color from body
			Object.assign(w.document.body.style, { backgroundColor: "transparent" });

			// clone styles from parent window into new window

			// Note: we're using a string here instead of `new CSSStyleSheet` because
			// the use of that constructor is currently not supported in Safari
			const inlineStyles = styles.reduce<string>((acc, cur) => {
				// flatten cssText for all rules in this stylesheet
				const cssText = [...cur.cssRules].map((rule) => rule.cssText).join("\n");

				return `${acc}\n${cssText}`;
			}, ``);

			Object.assign(w.document.body, {
				innerHTML: `
					<style>${inlineStyles}</style>
					${htmlContent}
				`,
			});

			// open print dialog
			w.print();

			// because the print dialog freezes the window
			// this will close the window when the print dialog is dismissed
			w.setTimeout(window.close, 0);
		};

		// open a blank window using the features listed above
		const w = window.open("", undefined, features.join(","));

		if (!w) {
			log("Could not open window to print", {}, "error");
			return;
		}

		const onLoad = onWindowLoad(w);

		// [Firefox] populate new window with content
		w.addEventListener("load", onLoad, false);

		// [Chrome] populate new window with content
		onLoad();
	}, [contentDiv, id]);
};
